import { styled } from '@mui/material/styles';
import { COLOR } from '@src/styles/color';

const iconBox = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 40,
  height: 40,
  borderRadius: 8,
  border: `1px solid ${COLOR.borderSlate}`,
  backgroundColor: COLOR.white,
  color: COLOR.darkSlate,
};

export const StyledTopPaidEvent = styled('div')(({ theme }) => ({
  maxWidth: 960,
  margin: '0 auto',
  padding: '24px 0 64px',
  display: 'flex',
  flexDirection: 'column',
  gap: 40,

  '.icon-box': iconBox,
  '.slate-text': { color: COLOR.graySlate },
  '.section-title': { color: COLOR.black.default, textAlign: 'center' },

  [theme.breakpoints.down('sm')]: {
    padding: '16px 0 64px',
    gap: 48,
  },
}));

export const StyledEventHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 16,
  textAlign: 'center',

  '.eyebrow': {
    color: COLOR.graySlate,
    fontStyle: 'italic',
    fontWeight: 400,
  },
  '.title': { color: COLOR.black.default },
  '.description': { color: COLOR.graySlate },

  [theme.breakpoints.down('sm')]: {
    gap: 12,
    '.eyebrow': { fontSize: 14, lineHeight: '17px' },
    '.title': { fontSize: 32, lineHeight: '35px' },
    '.description': { fontSize: 14, lineHeight: '20px' },
  },
}));

export const StyledAccumulation = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 720,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',

  '.stats-card': {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    padding: 20,
    borderRadius: 16,
    border: `1px solid ${COLOR.borderSlate}`,
    background: `linear-gradient(90deg, ${COLOR.lightSlate} 0%, ${COLOR.white} 100%)`,
  },
  '.stat': { flex: 1, display: 'flex', gap: 16, minWidth: 0 },
  '.stat-divider': {
    alignSelf: 'stretch',
    width: 1,
    backgroundColor: COLOR.borderSlate,
  },
  '.stat-body': { display: 'flex', flexDirection: 'column', gap: 8 },
  '.stat-value': { color: COLOR.darkSlate },
  '.stat-link': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: COLOR.graySlate,
    '&:hover': { textDecoration: 'underline' },
  },

  '.next-code-strip': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '-12px 10px 0',
    padding: '24px 16px 12px',
    borderRadius: '0 0 16px 16px',
    border: `0.5px solid ${COLOR.indigo[8]}`,
    background: `linear-gradient(90deg, ${COLOR.yellow[8]} 0%, ${COLOR.white} 100%)`,
    boxShadow: `0 4px 16px ${COLOR.boxShadow}`,
  },
  '.next-code-text': { flex: 1, color: COLOR.graySlate },
  '.next-code-amount': {
    color: COLOR.orange600,
    fontSize: 20,
    fontWeight: 700,
  },
  '.buy-more-button': { padding: '8px 12px', borderRadius: 8, flexShrink: 0 },

  [theme.breakpoints.down('sm')]: {
    '.stats-card': { flexDirection: 'column', alignItems: 'stretch' },
    '.stat-divider': { width: '100%', height: 1 },
    '.next-code-strip': {
      flexDirection: 'column',
      alignItems: 'stretch',
      margin: '-12px 0 0',
    },
    '.buy-more-button': { width: '100%' },
  },
}));

export const StyledPrizes = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,

  '.tagline': {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    marginLeft: 120,
  },
  '.tagline-arrow': { position: 'absolute', left: -60, top: 24 },
  // The English file comes untilted and 42px tall; match the Vietnamese one so the arrow lines up.
  '.tagline-tilted': { transform: 'rotate(-3deg)', margin: '6px 0 7px' },
  '.tagline-mobile': { display: 'none', maxWidth: '100%' },

  '.prize-list': {
    display: 'flex',
    justifyContent: 'center',
    gap: 80,
    width: '100%',
  },
  '.prize': {
    width: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
    padding: '8px 0',
    textAlign: 'center',
  },
  '.prize-image': { height: 160, maxWidth: '100%', objectFit: 'contain' },
  '.prize-text': { display: 'flex', flexDirection: 'column', gap: 4 },
  '.prize-name': { color: COLOR.darkSlate },

  [theme.breakpoints.down('md')]: {
    '.prize-list': { justifyContent: 'space-between', gap: 16 },
  },

  [theme.breakpoints.down('sm')]: {
    gap: 20,
    '.tagline': { display: 'none' },
    '.tagline-mobile': { display: 'block', marginTop: -12 },
    '.prize-list': {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 12,
    },
    '.prize': {
      width: 'auto',
      gap: 12,
      padding: 12,
      borderRadius: 16,
      backgroundColor: COLOR.lightSlate,
    },
    '.prize:first-of-type': { gridColumn: '1 / -1' },
    '.prize:first-of-type .prize-image': { height: 190 },
    '.prize:not(:first-of-type) .prize-image': { height: 116 },
  },
}));

export const StyledRules = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,

  '.rules-heading': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  '.period': { color: COLOR.graySlate, fontStyle: 'italic' },

  '.rules-card': {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 20,
    borderRadius: 16,
    backgroundColor: COLOR.lightSlate,
  },
  '.how-to-join': { color: COLOR.graySlate, textAlign: 'center' },
  '.steps': { display: 'flex', alignItems: 'center', gap: 8 },
  '.step': {
    flex: 1,
    alignSelf: 'stretch',
    display: 'flex',
    gap: 12,
    padding: 8,
  },
  '.step-body': { display: 'flex', flexDirection: 'column', gap: 8 },
  '.step-title': { color: COLOR.darkSlate },
  '.step-arrow': { color: COLOR.darkBlue, flexShrink: 0 },
  '.rules-divider': {
    height: 1,
    margin: '8px 0',
    backgroundColor: COLOR.borderSlate,
  },
  '.rule-details': {
    margin: 0,
    paddingLeft: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    color: COLOR.graySlate,
    b: { color: COLOR.darkGray, fontWeight: 500 },
    a: { color: COLOR.blueV2 },
  },

  [theme.breakpoints.down('md')]: {
    '.step': { flexDirection: 'column' },
  },

  [theme.breakpoints.down('sm')]: {
    '.rules-card': { padding: 16, gap: 20 },
    '.steps': { flexDirection: 'column', alignItems: 'stretch', gap: 20 },
    '.step': { flexDirection: 'row', padding: 0 },
    '.step-arrow': { display: 'none' },
    '.rules-divider': { margin: 0 },
    '.rule-details': { paddingLeft: 16, fontSize: 14, lineHeight: '20px' },
  },
}));

export const StyledRewardCodes = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  scrollMarginTop: 24,

  '.codes-heading': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  '.codes-heading .icon-box': {
    border: 'none',
    backgroundColor: COLOR.orange50,
    color: COLOR.orange600,
  },
  '.codes-table': {
    border: `0.5px solid ${COLOR.indigo[16]}`,
    borderRadius: 12,
    overflow: 'hidden',
    '.body-cell': { color: COLOR.darkGray },
  },
  '.code-cell': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    color: COLOR.darkSlate,
  },
  '.code-cards': { display: 'none' },
  '.code-card': {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 16,
    borderRadius: 12,
    border: `1px solid ${COLOR.borderSlate}`,
    backgroundColor: COLOR.white,
  },
  '.code-index': {
    alignSelf: 'flex-start',
    minWidth: 24,
    padding: '2px 6px',
    borderRadius: 6,
    textAlign: 'center',
    backgroundColor: COLOR.charcoal,
  },
  '.code-row': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  '.empty-codes': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    padding: '24px 0',
    color: COLOR.graySlate,
    img: { width: 200 },
  },

  [theme.breakpoints.down('sm')]: {
    gap: 20,
    '.codes-heading': { justifyContent: 'flex-start' },
    '.codes-table': { display: 'none' },
    '.code-cards': { display: 'flex', flexDirection: 'column', gap: 10 },
  },
}));
